/**
 * OAuth 2.0 Authorization endpoint — user-facing consent page (RFC 6749 §3.1).
 *
 * Validates query params, requires Inverio session (redirects to /login otherwise),
 * generates an authorization code on Authorize click, redirects back to client.
 *
 * Reachable at:
 *   - inverio.ai/oauth/authorize
 *   - mcp.inverio.ai/oauth/authorize  (falls through to SPA via Vercel)
 */

import { useEffect, useState, useCallback } from 'react';
import { View, Text, ActivityIndicator, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Shield, Check, X, AlertTriangle } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useIsAuthenticated, useAuth } from '@/lib/supabase/auth';
import { supabase } from '@/lib/supabase/client';
import { hapticLight, hapticSuccess } from '@/lib/haptics';
import { reportError } from '@/lib/errors';
import { isRegisteredRedirectUri } from '@/lib/oauth/clients-shared';

const C = {
  bg: '#0a1628',
  surface: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.1)',
  text: '#FFFFFF',
  text2: 'rgba(255,255,255,0.7)',
  text3: 'rgba(255,255,255,0.5)',
  brand: '#4fd1c5',
  red: '#FF6B6B',
  green: '#34D399',
};

const CODE_TTL_SECONDS = 5 * 60;

function generateCode(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export default function AuthorizePage() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    client_id?: string;
    redirect_uri?: string;
    response_type?: string;
    code_challenge?: string;
    code_challenge_method?: string;
    scope?: string;
    state?: string;
  }>();
  const { isAuthenticated, isLoading: authLoading } = useIsAuthenticated();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<{
    client_id: string;
    client_name: string;
    redirect_uris: string[];
    token_endpoint_auth_method: 'none' | 'client_secret_basic';
  } | null>(null);
  const [clientLoading, setClientLoading] = useState(true);

  const clientId = params.client_id;
  const redirectUri = params.redirect_uri;
  const responseType = params.response_type;
  const codeChallenge = params.code_challenge;
  const codeChallengeMethod = params.code_challenge_method;
  const scope = params.scope ?? 'mcp:*';
  const state = params.state;

  // Synchronous param-presence checks first
  useEffect(() => {
    if (!clientId) {
      setError('Missing client_id');
      setClientLoading(false);
      return;
    }
    if (!redirectUri) {
      setError('Missing redirect_uri');
      setClientLoading(false);
      return;
    }
    if (responseType !== 'code') {
      setError('Only response_type=code is supported');
      setClientLoading(false);
      return;
    }
    if (!codeChallenge) {
      setError('Missing code_challenge (PKCE required)');
      setClientLoading(false);
      return;
    }
    if (codeChallengeMethod !== 'S256') {
      setError('Only code_challenge_method=S256 is supported');
      setClientLoading(false);
      return;
    }
  }, [clientId, redirectUri, responseType, codeChallenge, codeChallengeMethod]);

  // Async client lookup against oauth_clients
  useEffect(() => {
    let cancelled = false;
    async function loadClient() {
      if (!clientId) return;
      setClientLoading(true);
      // Cast to any: oauth_clients/oauth_codes are not in the generated
      // Supabase types yet (same situation as the oauth_codes insert below).
      const { data, error: lookupErr } = await (supabase as any)
        .from('oauth_clients')
        .select('client_id, client_name, redirect_uris, token_endpoint_auth_method, status')
        .eq('client_id', clientId)
        .maybeSingle();
      if (cancelled) return;
      if (lookupErr || !data) {
        setError('Unknown client_id. The application must register at /oauth/register first.');
        setClientLoading(false);
        return;
      }
      if (data.status !== 'active') {
        setError('This client_id has been revoked.');
        setClientLoading(false);
        return;
      }
      const registeredUris = Array.isArray(data.redirect_uris)
        ? (data.redirect_uris as string[])
        : [];
      if (!redirectUri || !isRegisteredRedirectUri(redirectUri, registeredUris)) {
        setError('redirect_uri does not match any URI registered for this client.');
        setClientLoading(false);
        return;
      }
      setClient({
        client_id: data.client_id,
        client_name: data.client_name,
        redirect_uris: registeredUris,
        token_endpoint_auth_method: data.token_endpoint_auth_method,
      });
      setClientLoading(false);
    }
    void loadClient();
    return () => {
      cancelled = true;
    };
  }, [clientId, redirectUri]);

  // Auth gate — redirect to /login with return URL
  useEffect(() => {
    if (!authLoading && !isAuthenticated && Platform.OS === 'web') {
      const returnTo = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      router.replace(`/login?return_to=${returnTo}`);
    }
  }, [authLoading, isAuthenticated, router]);

  const handleAuthorize = useCallback(async () => {
    if (!user || !clientId || !redirectUri || !codeChallenge) return;

    setSubmitting(true);
    try {
      const code = generateCode();
      const expiresAt = new Date(Date.now() + CODE_TTL_SECONDS * 1000).toISOString();

      const { error: insertErr } = await supabase.from('oauth_codes').insert({
        code,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        user_id: user.id,
        client_id: clientId,
        redirect_uri: redirectUri,
        scope,
        expires_at: expiresAt,
      });
      if (insertErr) throw insertErr;

      hapticSuccess();

      const back = new URL(redirectUri);
      back.searchParams.set('code', code);
      if (state) back.searchParams.set('state', state);

      if (Platform.OS === 'web') {
        window.location.href = back.toString();
      }
    } catch (err) {
      reportError(err, { context: 'AuthorizePage.handleAuthorize' });
      setError('Failed to authorize. Please try again.');
      setSubmitting(false);
    }
  }, [user, clientId, redirectUri, codeChallenge, scope, state]);

  const handleCancel = useCallback(() => {
    if (!redirectUri) {
      router.replace('/');
      return;
    }
    hapticLight();
    const back = new URL(redirectUri);
    back.searchParams.set('error', 'access_denied');
    back.searchParams.set('error_description', 'User cancelled authorization');
    if (state) back.searchParams.set('state', state);

    if (Platform.OS === 'web') {
      window.location.href = back.toString();
    } else {
      router.replace('/');
    }
  }, [redirectUri, state, router]);

  if (authLoading || clientLoading || !isAuthenticated) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: C.bg }}
      >
        <ActivityIndicator color={C.brand} />
      </View>
    );
  }

  if (error) {
    return (
      <View
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: C.bg }}
      >
        <View
          className="rounded-xl p-6"
          style={{
            backgroundColor: C.surface,
            borderWidth: 1,
            borderColor: C.border,
            maxWidth: 480,
            width: '100%',
          }}
        >
          <View className="flex-row items-center gap-2 mb-3">
            <AlertTriangle size={20} color={C.red} />
            <Text
              style={{ color: C.red }}
              className="text-base font-bold flex-1"
              numberOfLines={1}
            >
              Invalid authorization request
            </Text>
          </View>
          <Text style={{ color: C.text2 }} className="text-sm leading-5">
            {error}
          </Text>
        </View>
      </View>
    );
  }

  const clientName = client?.client_name ?? clientId ?? 'an MCP client';

  return (
    <View
      className="flex-1 items-center justify-center px-6"
      style={{ backgroundColor: C.bg }}
    >
      <Animated.View
        entering={FadeInDown.duration(400)}
        className="rounded-xl p-6"
        style={{
          backgroundColor: C.surface,
          borderWidth: 1,
          borderColor: C.border,
          maxWidth: 480,
          width: '100%',
        }}
      >
        <View className="flex-row items-center gap-2 mb-4">
          <Shield size={22} color={C.brand} />
          <Text
            style={{ color: C.text }}
            className="text-lg font-bold flex-1"
            numberOfLines={1}
          >
            Authorize {clientName}
          </Text>
        </View>
        <Text style={{ color: C.text2 }} className="text-sm leading-5 mb-2">
          {clientName} is requesting access to your Inverio account through the Model
          Context Protocol.
        </Text>
        <Text style={{ color: C.text3 }} className="text-xs leading-5 mb-5">
          Granting access will let {clientName} call all 9 Inverio MCP tools on your
          behalf for 90 days. You can revoke this access anytime from Settings → API
          Keys.
        </Text>

        <AnimatedPressable
          onPress={handleAuthorize}
          disabled={submitting}
          className="flex-row items-center justify-center gap-2 py-3 rounded-md mb-2"
          style={{ backgroundColor: C.brand, opacity: submitting ? 0.5 : 1 }}
          accessibilityRole="button"
          accessibilityLabel={`Authorize ${clientName}`}
        >
          {submitting ? (
            <ActivityIndicator color="#0a1628" />
          ) : (
            <>
              <Check size={16} color="#0a1628" />
              <Text
                style={{ color: '#0a1628' }}
                className="text-sm font-bold"
                numberOfLines={1}
              >
                Authorize
              </Text>
            </>
          )}
        </AnimatedPressable>

        <AnimatedPressable
          onPress={handleCancel}
          disabled={submitting}
          className="flex-row items-center justify-center gap-2 py-3 rounded-md"
          style={{ borderWidth: 1, borderColor: C.border }}
          accessibilityRole="button"
          accessibilityLabel="Cancel authorization"
        >
          <X size={16} color={C.text2} />
          <Text
            style={{ color: C.text2 }}
            className="text-sm font-medium"
            numberOfLines={1}
          >
            Cancel
          </Text>
        </AnimatedPressable>
      </Animated.View>
    </View>
  );
}
